/**
 * Client HTTP tipizzato.
 *
 * Un solo posto in cui si mette il token e si traducono gli errori: nessun
 * componente deve ricordarsene, e il messaggio che finisce a schermo è quello
 * italiano preparato dal backend.
 */

import type {
  ContiDichiarati,
  OnboardingView,
  RegimeFiscale,
  AccountView,
  ActionView,
  ActionPreview,
  AlertView,
  AutonomyView,
  ChatBlock,
  ChatMessageView,
  ChatThreadView,
  ConversationView,
  DayOverview,
  GuestMessageView,
  IsoDate,
  ListingCalendarView,
  ConfrontoBaseline,
  AndamentoMese,
  ProfittoPortafoglio,
  Fascicolo,
  RiepilogoTrattenute,
  AllegatoView,
  ScadenzaView,
  StatoPropostaDocumento,
  CostoRicorrenteInput,
  CostoRicorrenteView,
  ListingFields,
  ListingView,
  ProfittoImmobile,
  RapportoInput,
  RapportoView,
  SuggestionView,
  TaskView,
} from "@otto/shared";

const TOKEN_KEY = "otto.token";

/**
 * L'intestazione con cui il server rimanda la sessione rinnovata.
 *
 * Le sessioni durano sette giorni e si rinnovano da sole a metà vita: se non
 * si raccoglie questa intestazione il rinnovo non arriva a destinazione, e il
 * token in mano scade lo stesso.
 */
const HEADER_SESSIONE = "x-otto-sessione";

/**
 * Cosa fare quando il server dice «questa sessione non vale più».
 *
 * Prima lo gestiva **solo** l'avvio: dopo una revoca — password cambiata da
 * un'altra parte, «esci da tutti i dispositivi», scadenza — ogni tap dava un
 * toast rosso e lo stream continuava a riconnettersi con un token morto, per
 * sempre. Adesso la reazione è una sola e succede una volta: si butta il
 * token e si torna all'accesso.
 */
type AscoltatoreScaduta = () => void;

let ascoltatori: AscoltatoreScaduta[] = [];
let sessioneGiaScaduta = false;

/** Registra chi vuole sapere che la sessione è finita. Restituisce come smettere. */
export function onSessioneScaduta(ascoltatore: AscoltatoreScaduta): () => void {
  ascoltatori.push(ascoltatore);
  return () => {
    ascoltatori = ascoltatori.filter((a) => a !== ascoltatore);
  };
}

/** Vero da quando il primo 401 è arrivato: lo stream lo legge per fermarsi. */
export function sessioneScaduta(): boolean {
  return sessioneGiaScaduta;
}

/** Solo per i test: rimette tutto com'era all'inizio. */
export function azzeraSessioneScaduta(): void {
  sessioneGiaScaduta = false;
  ascoltatori = [];
}

function dichiaraScaduta(): void {
  // Una volta sola: durante un caricamento partono cinque richieste insieme,
  // e cinque toast uno sopra l'altro non aiutano nessuno.
  if (sessioneGiaScaduta) return;
  sessioneGiaScaduta = true;
  setToken(null);
  for (const ascoltatore of ascoltatori) ascoltatore();
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* Modalità privata o storage disabilitato: si va avanti senza. */
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  // Sessione rinnovata a metà vita: il server ne manda una nuova e qui si
  // sostituisce, senza che l'host si accorga di niente.
  const rinnovata = response.headers.get(HEADER_SESSIONE);
  if (rinnovata) setToken(rinnovata);

  // Un 401 sulle rotte con sessione vuol dire «la sessione non vale più».
  // Sulle rotte di accesso vuol dire un'altra cosa — password sbagliata,
  // password attuale non corretta — e il motivo giusto lo scrive il server:
  // prima qui si diceva «la sessione è scaduta» a chi aveva solo sbagliato la
  // password, e sbagliando la password attuale nelle impostazioni si veniva
  // buttati fuori.
  //
  // E si esce solo se il rifiuto riguarda **la sessione di adesso**. Una
  // richiesta partita senza token, o con un token che nel frattempo è stato
  // sostituito, non dice niente di quella nuova: prima, dopo il link di
  // conferma, una richiesta partita un attimo prima tornava con 401 e
  // cancellava la sessione appena creata.
  if (response.status === 401 && !path.startsWith("/auth/")) {
    if (token != null && token === getToken()) dichiaraScaduta();
    throw new ApiError(
      401,
      "La sessione è scaduta: entra di nuovo.",
      "non_autorizzato",
    );
  }

  if (response.status === 204) return undefined as T;

  const body = (await response.json().catch(() => null)) as {
    error?: { message?: string; code?: string };
  } | null;

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body?.error?.message ?? "Qualcosa è andato storto.",
      body?.error?.code,
    );
  }

  return body as T;
}

export const api = {
  /**
   * Il controllo di salute, che ora dice solo `ok`.
   *
   * `devLogin` arriva solo quando è vero: serve a decidere se disegnare il
   * bottone «Entra senza password», che in produzione non esiste.
   */
  health: () =>
    request<{
      ok: true;
      devLogin?: boolean;
      /** Si può creare un account da soli, o si entra dalla landing. */
      registrazioneAperta?: boolean;
      /** Dove mandare chi non ha un account, a registrazione chiusa. */
      landing?: string;
    }>("/health"),

  async login(email: string, password: string): Promise<string> {
    const body = await request<{ token: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setToken(body.token);
    return body.token;
  },

  async cambiaPassword(attuale: string, nuova: string): Promise<void> {
    const body = await request<{ token: string }>("/auth/password", {
      method: "POST",
      body: JSON.stringify({ attuale, nuova }),
    });
    setToken(body.token);
  },

  /** Crea account e proprietario; il link di conferma arriva per email. */
  /**
   * Registrarsi. Di solito parte il link di conferma; con le email spente
   * (sviluppo, senza Resend) il server fa entrare subito e manda il token.
   */
  async registrati(dati: {
    nome: string;
    nomeAttivita: string;
    email: string;
    password: string;
  }): Promise<{ entrato: true } | { entrato: false; email: string }> {
    const body = await request<
      { entrato: true; token: string } | { inviata: true; email: string }
    >("/auth/registrati", {
      method: "POST",
      body: JSON.stringify(dati),
    });
    if ("token" in body) {
      setToken(body.token);
      return { entrato: true };
    }
    return { entrato: false, email: body.email };
  },

  /** Il link della conferma: verifica l'email ed entra. */
  async verificaEmail(biglietto: string): Promise<void> {
    const body = await request<{ token: string }>("/auth/verifica", {
      method: "POST",
      body: JSON.stringify({ biglietto }),
    });
    setToken(body.token);
  },

  rimandaVerifica: (email: string) =>
    request<{ inviata: true }>("/auth/verifica/rimanda", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  passwordDimenticata: (email: string) =>
    request<{ inviata: true }>("/auth/password/dimenticata", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  /** Il link della nuova password: la cambia ed entra. */
  async nuovaPassword(biglietto: string, nuova: string): Promise<void> {
    const body = await request<{ token: string }>("/auth/password/nuova", {
      method: "POST",
      body: JSON.stringify({ biglietto, nuova }),
    });
    setToken(body.token);
  },

  onboarding: () => request<{ onboarding: OnboardingView }>("/api/onboarding"),

  collegaGestionale: (clientId: string, clientSecret: string) =>
    request<{
      immobili: number;
      /** Case disattivate su Guesty: non entrano in Otto. */
      disattivati?: number;
      anteprima: Array<{
        nome: string;
        citta: string | null;
        foto: string | null;
      }>;
    }>("/api/onboarding/gestionale", {
      method: "POST",
      body: JSON.stringify({ clientId, clientSecret }),
    }),

  avviaImport: () =>
    request<{ onboarding: OnboardingView }>("/api/onboarding/importa", {
      method: "POST",
    }),

  completaOnboarding: () =>
    request<{ ok: true }>("/api/onboarding/completa", { method: "POST" }),

  logoutTutti: () =>
    request<{ ok: true }>("/auth/logout-tutti", { method: "POST" }),

  accessi: () =>
    request<{ accessi: Array<{ ip: string; riuscito: boolean; at: string }> }>(
      "/auth/accessi",
    ),

  async devLogin(): Promise<string> {
    const body = await request<{ token: string }>("/auth/dev-login", {
      method: "POST",
    });
    setToken(body.token);
    return body.token;
  },

  me: () =>
    request<{
      account: AccountView;
      autonomy: AutonomyView[];
      user: {
        id: string;
        email: string;
        nome: string | null;
        ruolo: "proprietario" | "collaboratore";
        creatoIl: string | null;
        terminiDaAccettare: boolean;
      };
    }>("/api/me"),
  accettaTermini: (versione: string) =>
    request<{ ok: boolean }>("/api/me/termini", {
      method: "POST",
      body: JSON.stringify({ versione }),
    }),

  listings: () => request<{ listings: ListingView[] }>("/api/listings"),
  listing: (id: string) =>
    request<{ listing: ListingView }>(`/api/listings/${id}`),
  calendar: (id: string, from: IsoDate, to: IsoDate) =>
    request<{ calendar: ListingCalendarView }>(
      `/api/listings/${id}/calendar?from=${from}&to=${to}`,
    ),
  confronto: (id: string, mese: string) =>
    request<{ confronto: ConfrontoBaseline }>(
      `/api/listings/${id}/confronto?mese=${mese}`,
    ),

  andamento: (id: string, periodo: { da: string; a: string }) =>
    request<{ andamento: AndamentoMese[] }>(
      `/api/listings/${id}/andamento?da=${periodo.da}&a=${periodo.a}`,
    ),
  trattenute: (id: string, periodo?: { da: string; a: string }) =>
    request<{ trattenute: RiepilogoTrattenute }>(
      `/api/listings/${id}/trattenute${
        periodo ? `?da=${periodo.da}&a=${periodo.a}` : ""
      }`,
    ),
  profitto: (id: string, mese: string) =>
    request<{ profitto: ProfittoImmobile }>(
      `/api/listings/${id}/profitto?mese=${mese}`,
    ),
  /** Le pulizie di un mese, in ore: `null` se l'host non le ha ancora date. */
  pulizieDelMese: (id: string, mese: string) =>
    request<{
      pulizie: {
        orarioCents: number;
        ore: number;
        importoCents: number;
      } | null;
    }>(`/api/listings/${id}/pulizie-mese?mese=${mese}`),
  salvaPulizieDelMese: (
    id: string,
    pulizie: { mese: string; orarioCents: number; ore: number },
  ) =>
    request<{ profitto: ProfittoImmobile }>(
      `/api/listings/${id}/pulizie-mese`,
      { method: "PUT", body: JSON.stringify(pulizie) },
    ),
  /** I conti di un mese che il gestionale non conosce, dati dall'host. */
  dichiaraConti: (id: string, conti: ContiDichiarati) =>
    request<{ profitto: ProfittoImmobile }>(
      `/api/listings/${id}/conti-dichiarati`,
      { method: "POST", body: JSON.stringify(conti) },
    ),
  /** Il fascicolo di un anno, per il commercialista. */
  fascicolo: (anno?: number) =>
    request<{ fascicolo: Fascicolo }>(
      `/api/fascicolo${anno ? `?anno=${anno}` : ""}`,
    ),
  /** Tutte le case insieme: una chiamata, non una per immobile. */
  portafoglio: (mese?: string) =>
    request<{ portafoglio: ProfittoPortafoglio }>(
      `/api/portafoglio${mese ? `?mese=${mese}` : ""}`,
    ),
  ricorrenti: (id: string) =>
    request<{ ricorrenti: CostoRicorrenteView[] }>(
      `/api/listings/${id}/ricorrenti`,
    ),
  creaRicorrente: (id: string, input: CostoRicorrenteInput) =>
    request<{ ricorrente: CostoRicorrenteView }>(
      `/api/listings/${id}/ricorrenti`,
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    ),
  chiudiRicorrente: (id: string, ricorrenteId: string) =>
    request<{ ok: true }>(`/api/listings/${id}/ricorrenti/${ricorrenteId}`, {
      method: "DELETE",
    }),
  rapporto: (id: string) =>
    request<{ rapporto: RapportoView | null }>(`/api/listings/${id}/rapporto`),
  salvaRapporto: (id: string, input: RapportoInput) =>
    request<{ rapporto: RapportoView }>(`/api/listings/${id}/rapporto`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  setCostoPulizia: (id: string, costoPuliziaCents: number | null) =>
    request<{ ok: true }>(`/api/listings/${id}/costo-pulizia`, {
      method: "PUT",
      body: JSON.stringify({ costoPuliziaCents }),
    }),
  setCin: (id: string, cin: string | null) =>
    request<{ ok: true }>(`/api/listings/${id}/cin`, {
      method: "PUT",
      body: JSON.stringify({ cin }),
    }),
  setFloorPrice: (id: string, floorPriceCents: number | null) =>
    request<{ ok: true }>(`/api/listings/${id}/floor-price`, {
      method: "PUT",
      body: JSON.stringify({ floorPriceCents }),
    }),

  threads: () => request<{ threads: ChatThreadView[] }>("/api/chat/threads"),
  createThread: () =>
    request<{ threadId: string }>("/api/chat/threads", { method: "POST" }),
  deleteThread: (threadId: string) =>
    request<{ ok: true }>(`/api/chat/threads/${threadId}`, {
      method: "DELETE",
    }),
  messages: (threadId: string) =>
    request<{ messages: ChatMessageView[] }>(
      `/api/chat/threads/${threadId}/messages`,
    ),
  briefing: () => request<{ block: ChatBlock | null }>("/api/chat/briefing"),

  actions: (status?: string, limit?: number) => {
    const query = new URLSearchParams();
    if (status) query.set("status", status);
    // Lo store ne teneva trenta, e una card in un thread vecchio non trovava
    // la sua azione: mostrava "Approva" su una cosa già eseguita.
    if (limit) query.set("limit", String(limit));
    const coda = query.toString();
    return request<{ actions: ActionView[] }>(
      `/api/actions${coda ? `?${coda}` : ""}`,
    );
  },
  action: (id: string) => request<{ action: ActionView }>(`/api/actions/${id}`),
  /** Il biglietto con cui lo stream si aggancia: vale un minuto. */
  streamTicket: () =>
    request<{ ticket: string; scadeFraMs: number }>("/api/stream/ticket", {
      method: "POST",
    }),
  approve: (id: string) =>
    request<{ actionId: string }>(`/api/actions/${id}/approve`, {
      method: "POST",
    }),
  reject: (id: string) =>
    request<{ actionId: string }>(`/api/actions/${id}/reject`, {
      method: "POST",
    }),
  rollback: (id: string) =>
    request<{ rollbackActionId: string }>(`/api/actions/${id}/rollback`, {
      method: "POST",
    }),

  conversations: (onlyAwaiting = false) =>
    request<{ conversations: ConversationView[] }>(
      `/api/conversations${onlyAwaiting ? "?stato=da_rispondere" : ""}`,
    ),
  conversation: (id: string) =>
    request<{ conversation: ConversationView; messages: GuestMessageView[] }>(
      `/api/conversations/${id}`,
    ),

  tasks: (from?: IsoDate, to?: IsoDate) =>
    request<{ tasks: TaskView[] }>(
      `/api/tasks${from ? `?from=${from}&to=${to ?? from}` : ""}`,
    ),

  alerts: (archivio = false) =>
    request<{ alerts: AlertView[]; nuovi: number }>(
      `/api/alerts${archivio ? "?archivio=1" : ""}`,
    ),
  alertsSeen: () =>
    request<{ visti: number }>("/api/alerts/seen", { method: "POST" }),
  resolveAlert: (id: string) =>
    request<{ ok: true }>(`/api/alerts/${id}/resolve`, { method: "POST" }),
  ignoreAlert: (id: string) =>
    request<{ ok: true }>(`/api/alerts/${id}/ignore`, { method: "POST" }),
  analyzeAlerts: () =>
    request<{ result: { nuovi: number; totali: number; risolti: number } }>(
      "/api/alerts/analyze",
      {
        method: "POST",
      },
    ),

  /** La modifica del testo diventa una proposta, non una scrittura. */
  proponiContenuto: (
    listingId: string,
    fields: ListingFields,
    mode: "sostituisci" | "aggiungi_in_coda" = "sostituisci",
  ) =>
    request<{ actionId: string; preview: ActionPreview }>(
      `/api/listings/${listingId}/contenuto`,
      {
        method: "POST",
        body: JSON.stringify({ fields, mode }),
      },
    ),

  suggestions: () =>
    request<{ suggestions: SuggestionView[] }>("/api/suggestions"),
  applySuggestion: (id: string) =>
    request<{ actionId: string }>(`/api/suggestions/${id}/apply`, {
      method: "POST",
    }),
  ignoreSuggestion: (id: string) =>
    request<{ ok: true }>(`/api/suggestions/${id}/ignore`, { method: "POST" }),
  analyzeListings: () =>
    request<{ result: { listings: number; created: number } }>(
      "/api/suggestions/analyze",
      {
        method: "POST",
      },
    ),

  overview: (date?: IsoDate) =>
    request<{ overview: DayOverview }>(
      `/api/overview${date ? `?date=${date}` : ""}`,
    ),

  setKillSwitch: (enabled: boolean) =>
    request<{ ok: true }>("/api/settings/kill-switch", {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),
  setFisco: (regime: RegimeFiscale | null, annoAvvio: number | null) =>
    request<{ ok: true }>("/api/settings/fisco", {
      method: "PUT",
      body: JSON.stringify({ regime, annoAvvio }),
    }),
  setDailyLimit: (limit: number) =>
    request<{ ok: true }>("/api/settings/daily-limit", {
      method: "PUT",
      body: JSON.stringify({ limit }),
    }),

  sync: () =>
    request<{ report: Record<string, number> }>("/api/sync", {
      method: "POST",
    }),

  quaderno: () =>
    request<{
      schede: SchedaView[];
      riscontro: RiscontroView;
      giro?: GiroQuaderno;
    }>("/api/quaderno"),
  /** Cancella l'account per sempre. Serve la password e la frase intera. */
  eliminaAccount: (password: string, conferma: string) =>
    request<{ ok: true }>("/api/account/elimina", {
      method: "POST",
      body: JSON.stringify({ password, conferma }),
    }),
  /** La pagina di Stripe dove l'host cambia carta, scarica fatture, disdice. */
  /**
   * Un allegato per la chat: i byte del file, grezzi, con il suo tipo. Il
   * messaggio poi porta solo l'id.
   */
  caricaAllegato: (file: File) =>
    request<{ allegato: AllegatoView }>(
      `/api/allegati?nome=${encodeURIComponent(file.name)}`,
      { method: "POST", headers: { "content-type": file.type }, body: file },
    ),
  statoPropostaDocumento: (id: string) =>
    request<{ stato: StatoPropostaDocumento }>(`/api/documenti/proposte/${id}`),
  salvaPropostaDocumento: (id: string) =>
    request<{
      giaDecisa: boolean;
      stato: StatoPropostaDocumento;
      costi: number;
      scadenze: number;
    }>(`/api/documenti/proposte/${id}/salva`, { method: "POST" }),
  scartaPropostaDocumento: (id: string) =>
    request<{ stato: StatoPropostaDocumento }>(
      `/api/documenti/proposte/${id}/scarta`,
      { method: "POST" },
    ),
  scadenze: (listingId?: string) =>
    request<{ scadenze: ScadenzaView[] }>(
      `/api/scadenze${listingId ? `?listingId=${encodeURIComponent(listingId)}` : ""}`,
    ),
  segnaPagata: (id: string) =>
    request<{ scadenza: ScadenzaView }>(`/api/scadenze/${id}/pagata`, {
      method: "POST",
    }),
  /** Un Checkout di Stripe sullo stesso cliente, per riaprire un abbonamento finito. */
  riattivaAbbonamento: () =>
    request<{ url: string }>("/api/abbonamento/riattiva", { method: "POST" }),
  portaleAbbonamento: () =>
    request<{ url: string }>("/api/abbonamento/portale", { method: "POST" }),
  /** Tenere la versione nuova di una scheda confermata, o quella di prima. */
  decidiAggiornamento: (id: string, tieniNuova: boolean) =>
    request<{ ok: boolean }>(`/api/quaderno/${id}/aggiornamento`, {
      method: "POST",
      body: JSON.stringify({ tieniNuova }),
    }),
  decidiScheda: (
    id: string,
    stato: "attiva" | "scartata",
    correzione?: string,
  ) =>
    request<{ ok: boolean }>(`/api/quaderno/${id}`, {
      method: "POST",
      body: JSON.stringify({ stato, correzione }),
    }),

  preferenze: () =>
    request<{ preferenze: PreferenzaView[] }>("/api/preferenze"),
  dimenticaPreferenza: (id: string) =>
    request<{ ok: boolean }>(`/api/preferenze/${id}`, { method: "DELETE" }),
  /** I propri dati, tutti in un file: è un diritto, non una funzione. */
  esportaDati: () => request<Record<string, unknown>>("/api/account/export"),

  chiudiAccount: () =>
    request<{ ok: true; messaggio: string }>("/api/account/chiudi", {
      method: "POST",
      body: JSON.stringify({ conferma: "chiudi il mio account" }),
    }),
};

/**
 * Una scheda del quaderno: come Otto risponderebbe a una domanda che torna.
 *
 * `citazioni` sono le frasi vere dell'host da cui è uscita. Non sono
 * decorazione: sono il modo in cui l'host può vedere **da dove viene** una
 * risposta prima di dire di sì. Un'inferenza sbagliata si corregge solo se si
 * vede da dove è venuta.
 */
export interface SchedaView {
  id: string;
  gruppo: string;
  lingua: string;
  /** La casa per cui vale; `null` quando vale per tutte le case. */
  listingId: string | null;
  /** Il nome della casa, letto nella stessa join: `null` per tutte le case. */
  casa: string | null;
  testo: string;
  nota: string;
  citazioni: { testo: string; at: string }[];
  suQuante: number;
  stato: "proposta" | "attiva" | "scartata";
  correzione: string | null;
  aggiornataIl: string;
  /** Una versione nuova di una scheda confermata, da scegliere. */
  aggiornamento?: string | null;
}

/** Per quale casa vale una scheda, detto come lo legge l'host. */
export function nomeCasa(s: Pick<SchedaView, "listingId" | "casa">): string {
  if (!s.listingId) return "Tutte le case";
  return s.casa ?? "Una casa senza nome";
}

/**
 * Le versioni della stessa domanda una accanto all'altra.
 *
 * L'ordine delle domande resta quello in cui arrivano (la più servita
 * prima); dentro una domanda, lingua per lingua, prima «Tutte le case» e poi
 * le case per nome. Sparse nell'elenco, due risposte al wifi di due case
 * diverse sembrano un doppione.
 */
export function perDomanda<T extends SchedaView>(schede: readonly T[]): T[] {
  const ordineGruppo = new Map<string, number>();
  const ordineLingua = new Map<string, number>();
  for (const s of schede) {
    if (!ordineGruppo.has(s.gruppo))
      ordineGruppo.set(s.gruppo, ordineGruppo.size);
    const chiave = `${s.gruppo}\u0000${s.lingua}`;
    if (!ordineLingua.has(chiave)) ordineLingua.set(chiave, ordineLingua.size);
  }
  return [...schede].sort(
    (a, b) =>
      ordineGruppo.get(a.gruppo)! - ordineGruppo.get(b.gruppo)! ||
      ordineLingua.get(`${a.gruppo}\u0000${a.lingua}`)! -
        ordineLingua.get(`${b.gruppo}\u0000${b.lingua}`)! ||
      Number(!!a.listingId) - Number(!!b.listingId) ||
      nomeCasa(a).localeCompare(nomeCasa(b), "it"),
  );
}

/**
 * Le schede che aspettano l'host: quelle nuove, e quelle confermate che
 * hanno una versione nuova accanto.
 */
export function daControllare(schede: readonly SchedaView[]): SchedaView[] {
  return schede.filter(
    (s) =>
      s.stato === "proposta" || (s.stato === "attiva" && !!s.aggiornamento),
  );
}

/** La chiave con cui una scheda si annuncia una volta sola in campanella. */
export function chiaveAvviso(s: SchedaView): string {
  return s.stato === "attiva" ? `${s.id}:agg:${s.aggiornataIl}` : s.id;
}

/**
 * Quanto il quaderno serve davvero.
 *
 * I due numeri vanno sempre insieme: l'accettazione da sola si trucca stando
 * zitti — basta preparare una bozza solo quando si è sicurissimi e va al 100%
 * mentre l'host continua a scrivere tutto a mano.
 *
 * `null` vuol dire «non abbastanza per dirlo», che non è zero.
 */
export interface RiscontroView {
  preparate: number;
  conScheda: number;
  senzaScheda: number;
  accettate: number;
  rifiutate: number;
  inAttesa: number;
  accettazione: number | null;
  copertura: number | null;
}

/** Una cosa che Otto ha imparato dell'host, come la manda l'API. */
export interface PreferenzaView {
  id: string;
  testo: string;
  /** La frase da cui è nata: serve a capire un'inferenza sbagliata. */
  origine: string | null;
  imparataIl: string;
}

/** A che punto è il giro settimanale del quaderno. */
export interface GiroQuaderno {
  /** L'ultima volta che ha trovato i gruppi di domande. */
  ultimo: string | null;
  /** Da quando può partire il prossimo (parte se sono arrivati messaggi). */
  prossimoDal: string | null;
  /** Senza chiave del modello il giro non parte. */
  automatico: boolean;
}
